import { useCallback, useEffect, useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { App as CapacitorApp } from '@capacitor/app'
import type { PluginListenerHandle } from '@capacitor/core'
import { FaceIdStatusScreen } from '@/components/biometric-lock/FaceIdStatusScreen'
import { UnlockSplash } from '@/components/biometric-lock/UnlockSplash'
import { PasscodeLockScreen } from '@/components/passcode/PasscodeLockScreen'
import {
  getCachedFaceIdEnabled,
  getFaceIdEnabled,
  isBiometryUsable,
  isFaceIdLockout,
  isVerifyInFlight,
  verifyFaceId,
} from '@/lib/face-id'
import { getCachedPasscodeEnabled, getPasscodeEnabled } from '@/lib/passcode'

interface BiometricLockProviderProps {
  children: React.ReactNode
}

// Outcome of the most recent unlock attempt in the current lock cycle:
// 'none' → no attempt yet (splash cover), 'failed' → auth failed or sheet
// canceled (retry screen), 'lockedOut' → iOS disabled biometry after too
// many failures (recovery screen).
type LockOutcome = 'none' | 'failed' | 'lockedOut'

export function BiometricLockProvider({ children }: BiometricLockProviderProps) {
  // bootstrap() in main.tsx prefetches the preferences, so the cached values
  // are usually available synchronously and we can skip the "checking" flash.
  const initialLocked =
    Capacitor.isNativePlatform() &&
    (getCachedFaceIdEnabled() === true || getCachedPasscodeEnabled() === true)
  const [isLocked, setIsLocked] = useState<boolean>(initialLocked)
  const [isReady, setIsReady] = useState(
    getCachedFaceIdEnabled() !== null && getCachedPasscodeEnabled() !== null
  )
  const [isPrompting, setIsPrompting] = useState(false)
  const [lastOutcome, setLastOutcome] = useState<LockOutcome>('none')
  // Passcode entry is the active unlock surface (passcode-only lock, or the
  // user tapped "Use passcode" after a biometric failure/lockout).
  const [passcodeMode, setPasscodeMode] = useState(false)
  const promptInFlightRef = useRef(false)
  // Refs mirror the lock state so the appStateChange handler (which fires as a
  // separate bridge event, possibly before React re-renders) reads current
  // values synchronously.
  const isLockedRef = useRef(initialLocked)
  const hasPromptedSinceLockRef = useRef(false)
  const lastOutcomeRef = useRef<LockOutcome>('none')

  const setOutcome = useCallback((value: LockOutcome) => {
    lastOutcomeRef.current = value
    setLastOutcome(value)
  }, [])

  const promptUnlock = useCallback(async () => {
    if (promptInFlightRef.current) return
    promptInFlightRef.current = true
    hasPromptedSinceLockRef.current = true
    setIsPrompting(true)
    try {
      await verifyFaceId('Unlock Pronteer')
      isLockedRef.current = false
      setIsLocked(false)
      setOutcome('none')
    } catch (error) {
      // Canceled and failed attempts both land on the retry screen; only a
      // biometry lockout gets the dedicated recovery screen.
      setOutcome(isFaceIdLockout(error) ? 'lockedOut' : 'failed')
    } finally {
      promptInFlightRef.current = false
      setIsPrompting(false)
    }
  }, [setOutcome])

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      setIsLocked(false)
      setIsReady(true)
      return
    }

    let cancelled = false
    let listener: PluginListenerHandle | undefined

    void (async () => {
      const [bioEnabled, passcodeEnabled] = await Promise.all([
        getFaceIdEnabled(),
        getPasscodeEnabled(),
      ])
      if (cancelled) return

      if (bioEnabled) {
        void promptUnlock()
      } else if (passcodeEnabled) {
        setPasscodeMode(true)
      } else {
        isLockedRef.current = false
        setIsLocked(false)
      }
      setIsReady(true)

      // Register regardless of the initial state so enabling Face ID in
      // settings arms the lock without needing an app relaunch.
      //
      // The Face ID system sheet itself resigns/reactivates the app, so both
      // branches must be inert while a prompt is in flight — otherwise the
      // sheet's own dismissal re-locks and re-prompts in a loop.
      listener = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) {
          // Lock synchronously on background so the iOS app-switcher snapshot
          // shows the lock overlay instead of app content, and there's no
          // content flash on resume. The module-level cache is updated
          // synchronously by the settings toggle, so it's current mid-session.
          // isVerifyInFlight covers prompts started outside this provider
          // (e.g. the settings toggle confirming before disabling) — their
          // sheets background the app the same way.
          if (
            (getCachedFaceIdEnabled() === true || getCachedPasscodeEnabled() === true) &&
            !promptInFlightRef.current &&
            !isVerifyInFlight()
          ) {
            isLockedRef.current = true
            hasPromptedSinceLockRef.current = false
            setIsLocked(true)
            // Drop back to the splash cover so the app-switcher snapshot never
            // shows the passcode pad; any half-entered digits die with the
            // unmounted entry component.
            setPasscodeMode(false)
            // Reset to the splash cover so the snapshot shows it — except a
            // biometry lockout, which survives backgrounding (Face ID is
            // still disabled by iOS until the device is passcode-unlocked).
            if (lastOutcomeRef.current !== 'lockedOut') {
              setOutcome('none')
            }
          }
          return
        }
        if (promptInFlightRef.current || isVerifyInFlight()) return
        // Resume never ARMS the lock (backgrounding did that) — it only
        // prompts for an existing lock, and only once per lock cycle so a
        // canceled prompt doesn't re-open itself when its sheet dismisses.
        // Re-read the preferences so toggling Face ID / the passcode off in
        // settings clears a pending lock.
        void Promise.all([getFaceIdEnabled(), getPasscodeEnabled()]).then(([bio, passcode]) => {
          if (!bio && !passcode) {
            isLockedRef.current = false
            setIsLocked(false)
            return
          }
          if (!bio) {
            // Passcode-only: no native sheet to open — just surface the pad
            // for the already-armed lock.
            if (isLockedRef.current) setPasscodeMode(true)
            return
          }
          if (isLockedRef.current && lastOutcomeRef.current === 'lockedOut') {
            // Lockout recovery: iOS re-enables biometry once the user
            // passcode-unlocks their device. Only after checkBiometry
            // confirms that may the once-per-cycle guard be reset —
            // re-prompting a still-locked-out sensor would instantly
            // re-throw biometryLockout in a loop.
            void isBiometryUsable().then((usable) => {
              if (!usable || promptInFlightRef.current || isVerifyInFlight() || !isLockedRef.current) {
                return
              }
              setOutcome('none')
              hasPromptedSinceLockRef.current = false
              void promptUnlock()
            })
            return
          }
          if (isLockedRef.current && !hasPromptedSinceLockRef.current) {
            void promptUnlock()
          }
        })
      })
    })()

    return () => {
      cancelled = true
      void listener?.remove()
    }
  }, [promptUnlock, setOutcome])

  const unlockWithPasscode = useCallback(() => {
    isLockedRef.current = false
    hasPromptedSinceLockRef.current = false
    setIsLocked(false)
    setPasscodeMode(false)
    setOutcome('none')
  }, [setOutcome])

  const showSplash = isPrompting || lastOutcome === 'none' || !isReady

  return (
    <>
      {children}
      {(isLocked || !isReady) &&
        (passcodeMode ? (
          <PasscodeLockScreen onUnlock={unlockWithPasscode} />
        ) : showSplash ? (
          <UnlockSplash />
        ) : (
          <FaceIdStatusScreen
            variant={lastOutcome === 'lockedOut' ? 'lockedOut' : 'failed'}
            isPrompting={isPrompting}
            onRetry={() => void promptUnlock()}
            onUsePasscode={
              getCachedPasscodeEnabled() === true ? () => setPasscodeMode(true) : undefined
            }
          />
        ))}
    </>
  )
}
